/**
 * Routing, and the providers every page depends on.
 *
 * The route table is the port of the Next.js file-system routes. The pages that do not exist
 * yet render a placeholder rather than being omitted: a 404 on `/generate` during the port
 * would look like a routing bug, where "not built yet" is the truth and is worth saying.
 *
 * **The admin pages are loaded lazily.** Between them they are the only thing in the app that
 * pulls in MUI X Data Grid, which roughly doubles the bundle — and they are reachable by a
 * handful of people. Splitting them out means a client never downloads the machinery for an
 * area they cannot open. This is the one place code-splitting is worth the indirection; the
 * client-facing pages stay eagerly loaded, because a spinner between "Generate" and "History"
 * would be a regression for the people who use the app every day.
 */

import { CssBaseline, ThemeProvider } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";

import { AuthProvider } from "@/auth/AuthProvider";
import { ApiClientError } from "@/api/client";
import { AppShell } from "@/components/layout/AppShell";
import { RequireAdmin } from "@/components/RequireAdmin";
import { RequireAuth } from "@/components/RequireAuth";
import { CentredSpinner } from "@/components/RequireAuth";
import { AboutPage } from "@/pages/AboutPage";
import { GeneratePage } from "@/pages/GeneratePage";
import { HistoryPage } from "@/pages/HistoryPage";
import { NotBuiltYet } from "@/pages/NotBuiltYet";
import { SignInPage } from "@/pages/SignInPage";
import { theme } from "@/theme/theme";

const AdminClientsPage = lazy(() =>
  import("@/pages/admin/AdminClientsPage").then((m) => ({ default: m.AdminClientsPage })),
);
const AdminGovernancePage = lazy(() =>
  import("@/pages/admin/AdminGovernancePage").then((m) => ({ default: m.AdminGovernancePage })),
);
const AdminHistoryPage = lazy(() =>
  import("@/pages/admin/AdminHistoryPage").then((m) => ({ default: m.AdminHistoryPage })),
);
const AdminRegisterPage = lazy(() =>
  import("@/pages/admin/AdminRegisterPage").then((m) => ({ default: m.AdminRegisterPage })),
);
const AdminUsagePage = lazy(() =>
  import("@/pages/admin/AdminUsagePage").then((m) => ({ default: m.AdminUsagePage })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /*
       * **Do not retry a 4xx.** A 403 means an admin has to act and a 401 means the token is
       * gone; retrying either is three guaranteed failures and three times the delay before
       * the user is told something they could have been told immediately. 5xx and network
       * errors are worth one retry, which is what the default of 3 is really for.
       */
      retry: (failureCount, error) => {
        if (error instanceof ApiClientError && error.status < 500) return false;
        return failureCount < 1;
      },
      // Preview URLs are short-lived signed URLs, so cached history goes stale in a way that
      // shows up as broken images rather than as merely old data. A minute is well inside
      // their hour-long window while still avoiding a refetch per navigation.
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route element={<AppShell />}>
                <Route path="/" element={<Navigate to="/generate" replace />} />
                <Route path="/sign-in" element={<SignInPage />} />
                <Route
                  path="/history"
                  element={
                    <RequireAuth>
                      <HistoryPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/generate"
                  element={
                    <RequireAuth>
                      <GeneratePage />
                    </RequireAuth>
                  }
                />
                <Route path="/about" element={<AboutPage />} />
                {/*
                  One `RequireAdmin` wrapping a layout route rather than five wrapping five
                  pages: the claim check force-refreshes the token, and doing that per page
                  would re-run it on every navigation inside the admin area.
                */}
                <Route
                  path="/admin"
                  element={
                    <RequireAdmin>
                      {/*
                        One boundary for the whole area rather than one per page: the chunk is
                        shared, so only the first admin page a caller opens ever waits.
                      */}
                      <Suspense fallback={<CentredSpinner label="Loading…" />}>
                        <Outlet />
                      </Suspense>
                    </RequireAdmin>
                  }
                >
                  <Route index element={<Navigate to="/admin/clients" replace />} />
                  <Route path="clients" element={<AdminClientsPage />} />
                  <Route path="history" element={<AdminHistoryPage />} />
                  <Route path="usage" element={<AdminUsagePage />} />
                  <Route path="governance" element={<AdminGovernancePage />} />
                  <Route path="register" element={<AdminRegisterPage />} />
                </Route>
                <Route path="*" element={<NotBuiltYet page="Not found" />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
